import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Grid, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import type { IContractToRegistrationProcessActivity } from '../../../classes/ContractToRegistrationProcessResponse';
import type { RegistrationStatuses } from '../../../services/rtkApi';
import {
    Activities,
    ConsultantReviewerRoles,
    useAssignProcessMutation,
    useGetContractToRegistrationQuery,
    useGetDirectLinkQuery,
} from '../../../services/rtkApi';
import { getRequiredConfig } from '@klpkonsern/appconfig';
import { generateRouteParams } from '@klpkonsern/routing';
import { showToast } from '@klpkonsern/ui';
import { Loader } from '@klp/design/Loader';
import {
    Tooltip,
    Block,
    Typography,
    Link,
    ActionButton,
    Heading,
} from '@klp/design';
import {
    getDynamics365URL,
    handleRequiredFormFields,
    useMapUserRolesToCandidateGroup,
} from '../../../utils';
import type { ILevendeKontrakterParams } from '@klp/levendekontrakter/views/LevendeKontrakter';
import { FormContainerKontraktsforvaltning } from '@klp/formgenerator/index';
import { contractregistrationSelectors } from '@klp/contractregistration/contractToRegistrationSlice';

type ControlActivities =
    | Activities.KONTROLLER_D365_REGISTRERING
    | Activities.UTFOR_REGISTRERING_D365;
interface ControlStepProps {
    handleCompleteStep: () => void;
    activity: {
        [key in ControlActivities]: IContractToRegistrationProcessActivity;
    };
    status: {
        [key in RegistrationStatuses.QA_UTFORT]: string;
    };
    prosessInstansId: string;
}

const useStyles = makeStyles(() => ({
    buttonWrapper: {
        width: 'max-content',
        cursor: 'not-allowed',
    },
}));

export default function ControlStep({
    handleCompleteStep,
    status,
    activity,
    prosessInstansId,
}: ControlStepProps) {
    const { t } = useTranslation();
    const anchorRef = useRef<HTMLButtonElement>(null);
    const { formFieldProcessing, formFieldError } = useSelector(
        contractregistrationSelectors,
    );
    const classes = useStyles();
    const { kontraktId } = useParams<{
        kontraktId: string;
        lang: string;
    }>();

    const {
        data: contractToRegistration,
        isLoading: contractToRegistrationLoading,
        isError: contractToRegistrationError,
    } = useGetContractToRegistrationQuery({
        kontraktId,
    });

    const [
        assignConsultant,
        { isLoading: assignConsultantLoading, isError: assignConsultantError },
    ] = useAssignProcessMutation();

    const { data: directLinkData, isError: directLinkError } =
        useGetDirectLinkQuery({
            dokumentId: kontraktId,
        });

    const { isUserCandidateForActivity } = useMapUserRolesToCandidateGroup(
        activity?.[Activities.KONTROLLER_D365_REGISTRERING],
    );

    const formFieldIsRequired = handleRequiredFormFields(
        activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.formFields,
    );

    const baseIsDisabled =
        (activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.id !== null &&
            activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.utfortDato !==
                null) ||
        activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.id === null ||
        assignConsultantLoading ||
        formFieldProcessing ||
        !isUserCandidateForActivity;

    const isDisabled =
        (activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.id !== null &&
            activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.utfortDato !==
                null) ||
        activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.id === null ||
        assignConsultantLoading ||
        formFieldProcessing ||
        !isUserCandidateForActivity ||
        formFieldError ||
        formFieldIsRequired;

    const isDisabledWithoutFormError = baseIsDisabled;

    const isLoading =
        assignConsultantLoading ||
        contractToRegistrationLoading ||
        formFieldProcessing;

    const newTabParams: ILevendeKontrakterParams = {
        autologin: true,
        hiddenNavbar: false,
    };

    const url = getRequiredConfig('WEBAPP_CONTRACT_FRAME_URL')
        .concat(`contract/${contractToRegistration?.lekoId}`)
        .concat(generateRouteParams(newTabParams));

    const handleOpenContract = () => {
        window.open(url, 'Contracts');
    };

    const handleComplete = async () => {
        const taskId = activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.id;
        const assignedEconomyConsultant =
            activity?.[Activities.UTFOR_REGISTRERING_D365]?.tildeltTil;

        if (!taskId || !status || !assignedEconomyConsultant) {
            return;
        }

        if (handleCompleteStep) {
            handleCompleteStep();
        }

        await assignConsultant({
            ansvarlig: assignedEconomyConsultant,
            fullforAktivitet: true,
            registreringStatus: status.QAUtfort,
            kontraktDokumentId: kontraktId,
            rolle: ConsultantReviewerRoles.QA_OKONOMI,
        });
    };

    if (assignConsultantError) {
        showToast({
            body: t('contractRegistration.error'),
            type: 'error',
        });
    }
    return (
        <>
            <Grid container={true} direction="row" justifyContent="flex-start">
                <Grid item={true}>
                    <Block
                        paddingLeft="compact"
                        paddingBottom="tiny"
                        paddingTop="compact"
                    >
                        <Heading
                            klpHeadingLevel="THREE"
                            klpColor="dark"
                            translation={`checkOffContract.step.${activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.taskDefinitionId}.controlRegistration`}
                        />
                    </Block>
                </Grid>
                <Grid item={true}>
                    <Block paddingLeft="compact" paddingTop="compact">
                        <ActionButton
                            actionType="external"
                            translation={`checkOffContract.step.${activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.taskDefinitionId}.D365Button`}
                            onClick={() =>
                                window.open(getDynamics365URL(), '_blank')
                            }
                        />
                    </Block>
                </Grid>
            </Grid>
            <Block paddingLeft="compact" paddingBottom="tiny">
                <Typography>
                    {t(
                        `checkOffContract.step.${activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.taskDefinitionId}.descriptionPartOne`,
                    )}
                </Typography>
            </Block>
            <Block paddingLeft="compact" paddingTop="tiny">
                {isLoading ? (
                    <Loader isLoading={isLoading} />
                ) : (
                    <>
                        {directLinkData?.kundeLenke && (
                            <Block paddingBottom="compact">
                                <Grid
                                    container={true}
                                    direction="row"
                                    justifyContent="flex-start"
                                >
                                    <>
                                        <Grid item={true}>
                                            <Block>
                                                {t(
                                                    `checkOffContract.step.${activity?.[Activities.UTFOR_REGISTRERING_D365]?.taskDefinitionId}.goToCustomer`,
                                                )}
                                            </Block>
                                        </Grid>
                                        <Grid item={true}>
                                            <Block paddingLeft="tiny">
                                                <Link
                                                    klpTypographyVariant="small"
                                                    klpColor="main"
                                                    disabled={directLinkError}
                                                    onClick={() =>
                                                        directLinkData?.kundeLenke &&
                                                        window.open(
                                                            directLinkData.kundeLenke,
                                                            '_blank',
                                                        )
                                                    }
                                                >
                                                    {' '}
                                                    <Typography>
                                                        {' '}
                                                        {
                                                            directLinkData?.kundeNavn
                                                        }{' '}
                                                        (
                                                        {
                                                            directLinkData?.kundeNummer
                                                        }
                                                        )
                                                    </Typography>
                                                </Link>
                                            </Block>
                                        </Grid>
                                    </>
                                </Grid>
                            </Block>
                        )}{' '}
                        {directLinkData?.kontraktLenke && (
                            <Block paddingBottom="tiny">
                                <Grid
                                    container={true}
                                    direction="row"
                                    justifyContent="flex-start"
                                >
                                    <>
                                        <Grid item={true}>
                                            <Block>
                                                {t(
                                                    `checkOffContract.step.${activity?.[Activities.UTFOR_REGISTRERING_D365]?.taskDefinitionId}.goToContractDraft`,
                                                )}
                                            </Block>
                                        </Grid>
                                        <Grid item={true}>
                                            <Block
                                                paddingTop="none"
                                                paddingLeft="tiny"
                                            >
                                                <Link
                                                    klpTypographyVariant="small"
                                                    klpColor="main"
                                                    disabled={directLinkError}
                                                    onClick={() =>
                                                        directLinkData?.kontraktLenke &&
                                                        window.open(
                                                            directLinkData.kontraktLenke,
                                                            '_blank',
                                                        )
                                                    }
                                                >
                                                    {' '}
                                                    <Typography>
                                                        {
                                                            directLinkData?.kontraktNummer
                                                        }
                                                    </Typography>
                                                </Link>
                                            </Block>
                                        </Grid>{' '}
                                    </>
                                </Grid>
                            </Block>
                        )}
                        {contractToRegistration?.lekoId && (
                            <Block paddingBottom="tiny">
                                <Grid
                                    container={true}
                                    direction="row"
                                    justifyContent="flex-start"
                                >
                                    <>
                                        <Grid item={true}>
                                            <Block>
                                                {t(
                                                    `checkOffContract.step.${activity?.[Activities.UTFOR_REGISTRERING_D365]?.taskDefinitionId}.goToLevendeKontrakter`,
                                                )}
                                            </Block>
                                        </Grid>
                                        <Grid item={true}>
                                            <Block
                                                paddingTop="none"
                                                paddingLeft="tiny"
                                            >
                                                <Link
                                                    klpTypographyVariant="small"
                                                    klpColor="main"
                                                    disabled={
                                                        contractToRegistrationError
                                                    }
                                                    onClick={() =>
                                                        contractToRegistration?.lekoId &&
                                                        handleOpenContract()
                                                    }
                                                >
                                                    {' '}
                                                    <Typography>
                                                        {
                                                            contractToRegistration?.lekoId
                                                        }
                                                    </Typography>
                                                </Link>
                                            </Block>
                                        </Grid>{' '}
                                    </>
                                </Grid>
                            </Block>
                        )}
                        {activity[Activities.KONTROLLER_D365_REGISTRERING]
                            ?.formFields && (
                            <Block paddingLeft="compact" paddingTop="tiny">
                                <Block>
                                    <FormContainerKontraktsforvaltning
                                        activity={
                                            activity?.[
                                                Activities
                                                    .KONTROLLER_D365_REGISTRERING
                                            ]
                                        }
                                        prosessInstansId={prosessInstansId}
                                        isDisabled={isDisabledWithoutFormError}
                                    />
                                </Block>
                            </Block>
                        )}
                        <Block paddingTop="compact">
                            {isDisabled ? (
                                <>
                                    <Block>
                                        <Tooltip
                                            translation="checkOffContract.tooltip"
                                            title=""
                                        >
                                            <Block
                                                className={
                                                    classes.buttonWrapper
                                                }
                                            >
                                                <Button
                                                    disabled={isDisabled}
                                                    variant="contained"
                                                    color="secondary"
                                                    onClick={handleComplete}
                                                >
                                                    {t(
                                                        `checkOffContract.step.${activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.taskDefinitionId}.approveRegistrationButton`,
                                                    )}
                                                </Button>
                                            </Block>
                                        </Tooltip>
                                    </Block>
                                </>
                            ) : (
                                <Block>
                                    <Button
                                        disabled={isLoading}
                                        ref={anchorRef}
                                        variant="contained"
                                        color="secondary"
                                        onClick={handleComplete}
                                    >
                                        {t(
                                            `checkOffContract.step.${activity?.[Activities.KONTROLLER_D365_REGISTRERING]?.taskDefinitionId}.approveRegistrationButton`,
                                        )}
                                    </Button>
                                </Block>
                            )}
                        </Block>
                    </>
                )}
            </Block>
        </>
    );
}
